import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography, Alert, Button, Box, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { File as ZestyMediaFile } from "../../../../../../shell/services/types";
import { fileExtension } from "../../utils/fileUtils";
import { dismissFileUploads, fileUploadStage } from "../../../../../../shell/store/media-revamp";
import { UploadHeaderText } from "../UploadModal";
import { AppState } from "../../../../../../shell/store/types";
import { UploadThumbnail } from "../UploadThumbnail";
import { mediaManagerApi } from "../../../../../../shell/services/mediaManager";
import { FileTypePreview } from "./FileTypePreview";
import { useGetCurrentUserRolesQuery } from "../../../../../../shell/services/accounts";
import instanceZUID from "../../../../../../utility/instanceZUID";
import { NoPermission } from "../../../../../../shell/components/NoPermission";
type ReplaceFileModalProps = {
  originalFile: ZestyMediaFile;
  onClose: () => void;
  onCancel: () => void;
};
export const ReplaceFileModal = ({
  onClose,
  originalFile,
  onCancel
}: ReplaceFileModalProps) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [newFile, setNewFile] = useState<File>(null);
  const [showUploadingFileModal, setShowUploadingFileModal] = useState(false);
  const hiddenFileInput = useRef(null);
  const {
    data: currentUserRoles
  } = useGetCurrentUserRolesQuery();
  const uploads = useSelector((state: AppState) => state.mediaRevamp.uploads);
  const filesToUpload = uploads.filter(upload => upload.status !== "failed");
  const canReplaceImage = currentUserRoles?.filter(role => role.entityZUID === instanceZUID)?.some(role => role.systemRole.delete);
  const acceptedExtension = fileExtension(originalFile?.url) === "jpg" || fileExtension(originalFile?.url) === "jpeg" ? ".jpg, .jpeg" : `.${fileExtension(originalFile?.url)}`;
  useEffect(() => {
    if (newFile) {
      dispatch(fileUploadStage([{
        file: newFile,
        bin_id: originalFile.bin_id,
        group_id: originalFile.group_id,
        replacementFile: true
      }]));
      setShowUploadingFileModal(true);
      setNewFile(null);
    }
  }, [newFile]);
  const handleCloseUploadingFileModal = () => {
    dispatch(dismissFileUploads());
    if (filesToUpload.length) {
      dispatch(mediaManagerApi.util.invalidateTags([{
        type: "GroupData",
        id: originalFile.group_id
      }, "BinFiles"]));
    }
    onClose();
  };
  if (!canReplaceImage) {
    return <NoPermission onClose={onCancel} headerTitle={t("media.replaceFileModalNoPermissionTitle")} headerSubtitle={t("media.replaceFileModalNoPermissionSubtitle")} />;
  }
  if (showUploadingFileModal) {
    return <Dialog open onClose={handleCloseUploadingFileModal} sx={{
      "& .MuiDialogContent-root": {
        width: 540,
        boxSizing: "border-box"
      }
    }}>
        <DialogTitle component="div" sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: theme => `1px solid ${theme.palette.border}`
      }}>
          <UploadHeaderText uploads={uploads} headerKeyword="replacedFile" showCount={false} />
          <IconButton onClick={handleCloseUploadingFileModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!!filesToUpload.length && <Box pt={2.5}>
              <UploadThumbnail file={filesToUpload.slice(-1)?.[0]} action="replace" originalFile={originalFile} showRemove={false} />
            </Box>}
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={handleCloseUploadingFileModal}>
            {t("common.done")}
          </Button>
        </DialogActions>
      </Dialog>;
  }
  return <>
      <Dialog open onClose={onCancel} maxWidth="xs" data-sentry-element="Dialog" data-sentry-source-file="ReplaceFileModal.tsx">
        <DialogTitle component="div" data-sentry-element="DialogTitle" data-sentry-source-file="ReplaceFileModal.tsx">
          <Box width={120} height={120} mb={1.5} sx={{
          "& .MuiBox-root": {
            borderRadius: 2
          }
        }} data-sentry-element="Box" data-sentry-source-file="ReplaceFileModal.tsx">
            <FileTypePreview src={originalFile?.url} filename={originalFile?.filename} updatedAt={originalFile?.updated_at} imageSettings={{
            width: 120
          }} data-sentry-element="FileTypePreview" data-sentry-source-file="ReplaceFileModal.tsx" />
          </Box>
          <Typography variant="h5" sx={{
          wordBreak: "break-all"
        }} data-sentry-element="Typography" data-sentry-source-file="ReplaceFileModal.tsx">
            <Box component="span" fontWeight={700} data-sentry-element="Box" data-sentry-source-file="ReplaceFileModal.tsx">
              {t("media.replaceFileModalTitle")}
              {":"}
            </Box>
            &nbsp;
            {originalFile?.filename}
          </Typography>
        </DialogTitle>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="ReplaceFileModal.tsx">
          <Alert severity="warning" data-sentry-element="Alert" data-sentry-source-file="ReplaceFileModal.tsx">{t("media.replaceFileModalWarning")}</Alert>
        </DialogContent>
        <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="ReplaceFileModal.tsx">
          <Button color="inherit" onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="ReplaceFileModal.tsx">
            {t("common.cancel")}
          </Button>
          <Button variant="contained" color="primary" onClick={() => {
          hiddenFileInput.current.value = "";
          hiddenFileInput.current.click();
        }} data-sentry-element="Button" data-sentry-source-file="ReplaceFileModal.tsx">
            {t("common.replace")}
          </Button>
        </DialogActions>
      </Dialog>
      <input type="file" ref={hiddenFileInput} onChange={evt => {
      setNewFile(evt.target.files[0]);
    }} hidden accept={acceptedExtension} style={{
      display: "none"
    }} />
    </>;
};